// React
import { useState, useEffect } from 'react';

// Utilities
import { useInitiativeDataStore } from 'utilities/store';
import { useLabels } from 'utilities/hooks';

const useSortBarState = ({ defaultSorts, customSorts, items }) => {
    // ///////////////////
    // STORE
    // ///////////////////

    const { utilities } = useInitiativeDataStore();

    // ///////////////////
    // HOOKS
    // ///////////////////
    const { label } = useLabels();

    // ///////////////////
    // STATE
    // ///////////////////

    const [allSorts, setAllSorts] = useState([]);
    const [sortBy, setSortBy] = useState();
    const [sortDirection, setSortDirection] = useState('asc');
    const [sortedItems, setSortedItems] = useState([]);

    // ///////////////////
    // DEFAULT SORTS
    // ///////////////////

    const sorts = {
        name: {
            name: 'name',
            label: label('ButtonSortByName'),
            method(items) {
                return items.sort((a, b) => a?.Name?.localeCompare(b?.Name));
            },
        },
        description: {
            name: 'description',
            label: label('ButtonSortByDescription'),
            method(items) {
                return items.sort((a, b) =>
                    a?.Description__c?.localeCompare(b?.Description__c)
                );
            },
        },
        lastModifiedDate: {
            name: 'lastModifiedDate',
            label: label('ButtonSortByDate'),
            method(items) {
                return items.sort(
                    (a, b) =>
                        new Date(a.LastModifiedDate) -
                        new Date(b.LastModifiedDate)
                );
            },
        },
        type: {
            name: 'type',
            label: label('ButtonSortByType'),
            method(items) {
                return items.sort((a, b) =>
                    a?.Type__c?.localeCompare(b?.Type__c)
                );
            },
        },
    };

    // ///////////////////
    // METHODS
    // ///////////////////

    function setSortState(name) {
        if (sortBy === name) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
            return;
        }
        setSortBy(name);
        setSortDirection('asc');
    }

    function sort() {
        let sorted =
            allSorts.find(x => x.name === sortBy)?.method(items) || items;

        if (sortDirection === 'desc') {
            sorted = sorted.reverse();
        }

        setSortedItems(sorted);
    }

    // ///////////////////
    // EFFECTS
    // ///////////////////

    useEffect(() => {
        const all = [
            ...defaultSorts.map(x => sorts[x]),
            ...(customSorts ?? []),
        ];
        setSortBy(all[0].name);
        setAllSorts(all);
    }, []);

    useEffect(() => {
        sort();
    }, [sortBy, sortDirection, utilities.initiative.get().Id]);

    // ///////////////////
    // RETURN
    // ///////////////////

    return {
        allSorts,
        sortedItems,
        setSortBy,
        sortBy,
        sortDirection,
        setSortState,
    };
};

export default useSortBarState;
